/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date: Sep 18 2023 00:00:00 PM
 *
 *******************************************************/
//flow

import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { routes } from 'src/routes';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import history from 'src/routes/history';
import { Link } from '@shopify/polaris';

import Notification from 'src/components/containers/dashboard/notifications/notification';

type Props = {
  subscription: Object
};

type ComposedProps = Props & WithI18nProps;

/* Region: BFCM2021 */
const renderSpecialDiscount2021ExpiredSoon: React.FC<ComposedProps> = (
  subscription: Object,
  i18n: Object
) => {
  const { dayLeft } = subscription;

  // Uppercase first character (fix for german text, since duration appears at the beginning of string)
  let duration = i18n.translate(`AdminHTML.Common.Duration.dayV3`, {
    count: dayLeft,
    nt_number: i18n.formatNumber(dayLeft)
  });

  const content = `<div>
      <p>
        ${i18n.translate('AdminHTML.Common.SpecialDiscountExpiredSoonNotification.content_1', {
          t1: `<strong>${duration}</strong>`
        })}
      </p>
      <p>
        ${i18n.translate('AdminHTML.Common.Subscription.preventInterruptions', {
          t1: `<strong>${i18n.translate('AdminHTML.Common.Subscription.reSubscribe')}</strong>`
        })}
      </p>
    </div>`;

  const notification = {
    type: 'warning',
    allowDismiss: false,
    title: i18n.translate('AdminHTML.Common.SpecialDiscountExpiredSoonNotification.title'),
    content,
    actionLabel: i18n.translate('AdminHTML.Common.Action.subscribeNow'),
    action: routes.subscription.package()
  };

  return (
    <div className="exceededPlanNotification">
      <Notification status={notification.type} notification={notification} />
    </div>
  );
};

const renderSpecialDiscountExpired = i18n => {
  const content = `<div>
      <p>
        ${i18n.translate('AdminHTML.Common.SpecialDiscountExpiredNotification.content_1', {
          t1: `<strong>${i18n.translate(
            'AdminHTML.Common.SpecialDiscountExpiredNotification.content_1_t1'
          )}</strong>`
        })}
      </p>
      <p>
        ${i18n.translate('AdminHTML.Common.Subscription.preventInterruptions', {
          t1: `<strong>${i18n.translate('AdminHTML.Common.Subscription.reSubscribe')}</strong>`
        })}
      </p>
    </div>`;
  const notification = {
    type: 'warning',
    allowDismiss: false,
    title: i18n.translate('AdminHTML.Common.SpecialDiscountExpiredNotification.title'),
    content,
    actionLabel: i18n.translate('AdminHTML.Common.Action.subscribeNow'),
    action: routes.subscription.plan()
  };

  return (
    <div className="exceededPlanNotification">
      <Notification status={notification.type} notification={notification} />
    </div>
  );
};
/* End-Region: BFCM2021 */

const renderSpecialDiscountExpiredSoon: React.FC<ComposedProps> = (
  subscription: Object,
  tenantApp: Object,
  i18n: Object
) => {
  const { dayLeft } = subscription;

  const numberProducts = _.get(tenantApp, 'appParams.currentTotalProducts', 0);
  // Uppercase first character (fix for german text, since duration appears at the beginning of string)
  let duration = i18n.translate(`AdminHTML.Common.Duration.dayV2`, {
    count: dayLeft,
    nt_number: i18n.formatNumber(dayLeft)
  });

  const content = `<div>
      <p>
        ${i18n.translate('AdminHTML.Common.SpecialDiscountExpiredSoonNotification.content_1', {
          t1: `<strong>${duration}</strong>`
        })}
      </p>
      <p style="margin-top: 5px">
        ${i18n.translate('AdminHTML.Common.SpecialDiscountExpiredSoonNotification.content_2', {
          t1: `<strong>${i18n.translate(
            'AdminHTML.Common.SpecialDiscountExpiredSoonNotification.auto_resubscribe'
          )}</strong>`
        })}
      </p>
      ${
        numberProducts >= 50000
          ? `<p style="margin-top: 5px">
        ${i18n.translate('AdminHTML.Common.SpecialDiscountExpiredSoonNotification.content_3')}
      </p>`
          : ''
      }
    </div>`;

  let notification = {
    type: 'warning',
    allowDismiss: false,
    title: i18n.translate('AdminHTML.Common.SpecialDiscountExpiredSoonNotification.title'),
    content
  };

  if (numberProducts >= 50000) {
    notification = {
      ...notification,
      actionLabel: i18n.translate('AdminHTML.Common.Action.contactUs'),
      action: 'beacon'
    };
  } else {
    notification = {
      ...notification,
      actionLabel: i18n.translate('AdminHTML.Common.Action.subscribeNow'),
      action: routes.subscription.plan()
    };
  }
  return (
    <div className="exceededPlanNotification">
      <Notification status={notification.type} notification={notification} />
    </div>
  );
};

const renderSpecialDiscountAfterExpired = (
  tenantApp: Object,
  codeDisplay: string,
  i18n: Object
) => {
  const numberProducts: number = _.get(tenantApp, 'appParams.currentTotalProducts', 0);
  const content: string = `<div>
      <p>
        ${i18n.translate('AdminHTML.Common.SpecialDiscountAfterExpiredNotification.content_1', {
          code: `<strong>${codeDisplay}</strong>`,
          t1: `<strong>${i18n.translate(
            'AdminHTML.Common.SpecialDiscountAfterExpiredNotification.auto_resubscribe'
          )}</strong>`,
          t2: `${i18n.translate(
            'AdminHTML.Common.SpecialDiscountAfterExpiredNotification.preventInterruptions'
          )}`
        })}
      </p>
      <p style="margin-top: 5px">
      ${
        numberProducts >= 50000
          ? `<p style="margin-top: 5px">
        ${i18n.translate('AdminHTML.Common.SpecialDiscountAfterExpiredNotification.content_2')}
      </p>`
          : ''
      }
    </p>
    </div>`;
  let notification = {
    type: 'warning',
    allowDismiss: false,
    title: i18n.translate('AdminHTML.Common.SpecialDiscountAfterExpiredNotification.title'),
    content
  };
  if (numberProducts >= 50000) {
    notification = {
      ...notification,
      actionLabel: i18n.translate('AdminHTML.Common.Action.contactUs'),
      action: 'beacon'
    };
  }
  return (
    <div className="exceededPlanNotification">
      <Notification status={notification.type} notification={notification} />
    </div>
  );
};

/*Region: XMAS22 / PREMIUM23 */

const renderSpecialDiscount02ExpiredSoon: React.FC<ComposedProps> = (
  subscription: Object,
  tenantApp: Object,
  codeDisplay: string,
  i18n: Object
) => {
  const { dayLeft } = subscription;

  const numberProducts: number = _.get(tenantApp, 'appParams.currentTotalProducts', 0);
  // Uppercase first character (fix for german text, since duration appears at the beginning of string)
  let duration = i18n.translate(`AdminHTML.Common.Duration.dayV2`, {
    count: dayLeft,
    nt_number: i18n.formatNumber(dayLeft)
  });

  const contentHtml = (
    <div>
      <p>
        {i18n.translate('AdminHTML.Common.SpecialDiscount02ExpiredSoonNotification.content_1', {
          t1: (
            <strong>
              {i18n.translate(
                'AdminHTML.Common.SpecialDiscount02ExpiredSoonNotification.autoResubscribe'
              )}
            </strong>
          )
        })}
      </p>

      {numberProducts >= 50000 ? (
        dayLeft <= 7 ? (
          <p style={{ marginTop: '5px' }}>
            {i18n.translate(
              'AdminHTML.Common.SpecialDiscount02ExpiredSoonNotification.contentLarge03'
            )}
          </p>
        ) : (
          <p style={{ marginTop: '5px' }}>
            {i18n.translate(
              'AdminHTML.Common.SpecialDiscount02ExpiredSoonNotification.contentLarge02'
            )}
          </p>
        )
      ) : (
        <p style={{ marginTop: '5px' }}>
          {i18n.translate('AdminHTML.Common.SpecialDiscount02ExpiredSoonNotification.content_2', {
            here: (
              <Link onClick={() => history.push(routes.subscription.plan())}>
                <em>{i18n.translate('AdminHTML.Common.Action.here')}</em>
              </Link>
            )
          })}
        </p>
      )}
    </div>
  );

  let notification = {
    type: 'warning',
    allowDismiss: false,
    title: i18n.translate(
      `AdminHTML.Common.SpecialDiscount02ExpiredSoonNotification.${
        dayLeft <= 7 ? 'title' : 'titleBefore15Days'
      }`,
      {
        t1: duration,
        code: codeDisplay
      }
    ),
    contentHtml
  };

  if (numberProducts >= 50000) {
    notification = {
      ...notification,
      actionLabel: i18n.translate('AdminHTML.Common.Action.contactUs'),
      action: 'beacon'
    };
  }
  return (
    <div className="exceededPlanNotification">
      <Notification status={notification.type} notification={notification} />
    </div>
  );
};

const renderSpecialDiscount02Expired: React.FC<ComposedProps> = (
  tenantApp: Object,
  codeDisplay: string,
  i18n: Object
) => {
  const numberProducts: number = _.get(tenantApp, 'appParams.currentTotalProducts', 0);

  const contentHtml = (
    <div>
      {/* <p>
          {i18n.translate('AdminHTML.Common.SpecialDiscount02ExpiredSoonNotification.content_1', {
            t1: (
              <strong>
                {i18n.translate(
                  'AdminHTML.Common.SpecialDiscount02ExpiredSoonNotification.autoResubscribe'
                )}
              </strong>
            )
          })}
        </p> */}

      {numberProducts >= 50000 ? (
        <p style={{ marginTop: '5px' }}>
          {i18n.translate('AdminHTML.Common.SpecialDiscount02ExpiredNotification.contentLarge')}
        </p>
      ) : (
        <p style={{ marginTop: '5px' }}>
          {i18n.translate('AdminHTML.Common.SpecialDiscount02ExpiredNotification.content', {
            here: (
              <Link onClick={() => history.push(routes.subscription.plan())}>
                <em>{i18n.translate('AdminHTML.Common.Action.here')}</em>
              </Link>
            )
          })}
        </p>
      )}
    </div>
  );

  let notification = {
    type: 'warning',
    allowDismiss: false,
    title: i18n.translate('AdminHTML.Common.SpecialDiscount02ExpiredNotification.title', {
      code: codeDisplay
    }),
    contentHtml
  };

  if (numberProducts >= 50000) {
    notification = {
      ...notification,
      actionLabel: i18n.translate('AdminHTML.Common.Action.contactUs'),
      action: 'beacon'
    };
  }
  return (
    <div className="exceededPlanNotification">
      <Notification status={notification.type} notification={notification} />
    </div>
  );
};

const renderSpecialDiscount02AfterExpired = (tenantApp: Object, i18n: Object) => {
  const { specialDiscountExpiredAt } = tenantApp;

  const expiredDate: string = moment(new Date(specialDiscountExpiredAt * 1000)).format(
    'MMM DD, YYYY'
  );

  const numberProducts: number = _.get(tenantApp, 'appParams.currentTotalProducts', 0);
  const content =
    numberProducts < 50000
      ? `<div>
      <p>
        ${i18n.translate('AdminHTML.Common.SpecialDiscount02AfterExpiredNotification.content_1', {
          date: `<strong>${expiredDate}</strong>`
        })}
      </p>
    </div>`
      : `
    <div>
      <p>
        ${i18n.translate(
          'AdminHTML.Common.SpecialDiscount02AfterExpiredNotification.contentLarge_01',
          {
            date: `<strong>${expiredDate}</strong>`
          }
        )}
      </p>
      <p style="margin-top: 5px">
        ${i18n.translate(
          'AdminHTML.Common.SpecialDiscount02AfterExpiredNotification.contentLarge_02',
          {
            date: `<strong>${expiredDate}</strong>`
          }
        )}
      </p>
    </div>`;
  let notification = {
    type: 'warning',
    allowDismiss: false,
    title: i18n.translate('AdminHTML.Common.SpecialDiscount02AfterExpiredNotification.title'),
    content
  };
  if (numberProducts >= 50000) {
    notification = {
      ...notification,
      actionLabel: i18n.translate('AdminHTML.Common.Action.contactUs'),
      action: 'beacon'
    };
  }
  return (
    <div className="exceededPlanNotification">
      <Notification status={notification.type} notification={notification} />
    </div>
  );
};
/* End-Region: XMAS22 / PREMIUM23 / BOOSTAI23 */

const PackageSpecialDiscountBanner: React.FC<ComposedProps> = ({
  subscription,
  tenantApp,
  i18n
}) => {
  const { status, afterDayDiscountExpired, discountCode } = subscription;

  const code = _.get(discountCode, 'code', '');
  // Show code BFCM22 or VETERAN22 infos without prefix ex:VETERAN22-a only show VETERAN22
  const codeDisplay = code.split('-')[0];
  let component = {
    specialDiscount2021ExpiredSoon: renderSpecialDiscount2021ExpiredSoon(subscription, i18n),
    oldSpecialDiscountExpired: renderSpecialDiscountExpired(i18n),
    specialDiscountExpiredSoon: renderSpecialDiscountExpiredSoon(subscription, tenantApp, i18n),
    specialDiscountAfterExpired: renderSpecialDiscountAfterExpired(tenantApp, codeDisplay, i18n)
  };
  const isSpecialDiscount = [
    'XMAS22',
    'PREMIUM23',
    'BOOSTAI23',
    'BOOSTAI15',
    'BOOSTAI20',
    'BFCM23'
  ].find(element => code.includes(element));
  if (isSpecialDiscount) {
    component = {
      specialDiscountExpiredSoon: renderSpecialDiscount02ExpiredSoon(
        subscription,
        tenantApp,
        codeDisplay,
        i18n
      ),
      specialDiscountAfterExpired: renderSpecialDiscount02AfterExpired(tenantApp, i18n),
      specialDiscountExpired: renderSpecialDiscount02Expired(tenantApp, codeDisplay, i18n)
    };
  }

  return component[afterDayDiscountExpired ? afterDayDiscountExpired : status] || null;
};

const mapStateToProps = state => ({
  subscription: _.get(state.oauth, 'subscription', {}),
  tenantApp: _.get(state.oauth, 'tenantApp', {})
});

export default connect(mapStateToProps)(withI18n()(PackageSpecialDiscountBanner));
